import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid } from 'grommet';
import { MetricCard } from '@Components/Partial/Product/Analytics';
import {
  StyledPanel,
  StyledScrollWrap,
  StyledTable,
} from './StyledCidAnalyticsDashboardPage';
import {
  WEEKLY_COLORS, EST_REVENUE_COLORS,
  formatMoney, formatViews,
  PanelTitle, EmptyTable,
} from './CidAnalyticsShared';
import { WeeklyGrowthChart, WeeklyViewsChart } from './CidAnalyticsCharts';

export default function CidAnalyticsWeeklyTab({
  weeklyGrowth,
  viewsAnalytics,
  estimatedRevenue,
  loading,
  page,
  small,
  hasFilters,
}) {
  const totalNewCreators = React.useMemo(
    () => weeklyGrowth?.reduce((s, r) => s + (r.newCreators || 0), 0) ?? null,
    [weeklyGrowth],
  );
  const totalNewAssets = React.useMemo(
    () => weeklyGrowth?.reduce((s, r) => s + (r.newAssets || 0), 0) ?? null,
    [weeklyGrowth],
  );
  const totalEngagedViews = React.useMemo(
    () => viewsAnalytics?.byWeek?.reduce((s, r) => s + (r.engagedViews || 0), 0) ?? null,
    [viewsAnalytics],
  );

  return (
    <Box gap="1rem">

      {/* Weekly KPI cards */}
      <Grid columns={small ? ['1fr', '1fr'] : ['1fr', '1fr', '1fr']} gap="1rem">
        <MetricCard
          label="TOTAL NEW CREATORS"
          value={totalNewCreators !== null ? String(totalNewCreators) : 'Data unavailable'}
          subLabel={hasFilters ? 'new creator labels — selected period' : 'new creator labels — all time'}
          textColor={WEEKLY_COLORS.newCreators}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={WEEKLY_COLORS.newCreators}
        />
        <MetricCard
          label="TOTAL NEW ASSETS"
          value={totalNewAssets !== null ? String(totalNewAssets) : 'Data unavailable'}
          subLabel={hasFilters ? 'new reference files — selected period' : 'new reference files — all time'}
          textColor={WEEKLY_COLORS.newAssets}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={WEEKLY_COLORS.newAssets}
        />
        <MetricCard
          label="TOTAL ENGAGED VIEWS"
          value={totalEngagedViews !== null ? formatViews(totalEngagedViews) : 'Data unavailable'}
          subLabel={hasFilters ? 'from revenue reports — selected period' : 'from revenue reports — all time'}
          textColor={WEEKLY_COLORS.engagedViews}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={WEEKLY_COLORS.engagedViews}
        />
      </Grid>

      {/* Weekly growth bar chart */}
      <StyledPanel gap="0.75rem">
        <PanelTitle label="New Creators & Assets per Week" />
        <WeeklyGrowthChart data={weeklyGrowth} loading={loading} />
      </StyledPanel>

      {/* Engaged views area chart */}
      <StyledPanel gap="0.75rem">
        <PanelTitle label="Engaged Views per Week" />
        <WeeklyViewsChart data={viewsAnalytics?.byWeek ?? null} loading={loading} />
      </StyledPanel>

      {/* Engaged views per creator table */}
      <StyledPanel gap="0.75rem">
        <PanelTitle label="Engaged Views per Creator" />
        {viewsAnalytics?.byCreator && viewsAnalytics.byCreator.length > 0 ? (
          <StyledScrollWrap>
            <StyledTable>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', width: '40px' }}>#</th>
                  <th style={{ textAlign: 'left' }}>Asset Label</th>
                  <th>Engaged Views</th>
                  <th>Total Views</th>
                  <th>Est. Revenue</th>
                </tr>
              </thead>
              <tbody>
                {viewsAnalytics.byCreator.map((row, i) => {
                  const estEntry = estimatedRevenue?.byCreator?.find(
                    (e) => e.assetLabel === row.assetLabel,
                  );
                  return (
                    <tr key={row.assetLabel}>
                      <td style={{ textAlign: 'left', color: 'var(--page-hint-text)' }}>{i + 1}</td>
                      <td style={{ textAlign: 'left' }}>{row.assetLabel}</td>
                      <td style={{ color: WEEKLY_COLORS.engagedViews, fontWeight: 600 }}>
                        {formatViews(row.totalEngagedViews)}
                      </td>
                      <td>{formatViews(row.totalViews)}</td>
                      <td style={{ color: EST_REVENUE_COLORS.totalEstimatedRevenue }}>
                        {estEntry ? formatMoney(estEntry.totalEstimatedRevenue) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </StyledTable>
          </StyledScrollWrap>
        ) : <EmptyTable page={page} loading={loading} />}
      </StyledPanel>

    </Box>
  );
}

CidAnalyticsWeeklyTab.propTypes = {
  weeklyGrowth: PropTypes.array,
  viewsAnalytics: PropTypes.shape({
    byWeek: PropTypes.array,
    byCreator: PropTypes.array,
  }),
  estimatedRevenue: PropTypes.shape({
    byCreator: PropTypes.array,
  }),
  loading: PropTypes.bool.isRequired,
  page: PropTypes.object.isRequired,
  small: PropTypes.bool.isRequired,
  hasFilters: PropTypes.bool.isRequired,
};
